import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Job } from '@generated/base-types';
import { takeGraphQLResult } from '@new/common/operators/take-graphql-response';
import { parseError } from '@new/common/utils/error-parser';
import { LoadExamCandidatesGQL } from '@new/pools/pool/lib/services/exam/load-exam-candidates.generated';
import { TranslateService } from '@ngx-translate/core';
import { Action, State, type StateContext } from '@ngxs/store';
import {
  catchError,
  filter,
  interval,
  map,
  mapTo,
  Observable,
  switchMap,
  take,
  tap,
  throwError
} from 'rxjs';
import { ToastsService } from '../../../../common/services/toasts.service';
import { JobGQL } from '../../lib/services/candidates/job.generated';
import { GenerateMasterAccessDocumentGQL } from '../../lib/services/candidates/mutations/create-master-document.generated';
import { DeleteCandidatesGQL } from '../../lib/services/candidates/mutations/delete-candidate.generated';
import * as Act from './exam-candidates.actions';
import {
  examCandidatesStateDefaults,
  ExamCandidatesStateModel
} from './exam-candidates.state.model';

@State<ExamCandidatesStateModel>({
  name: 'examCandidates',
  defaults: examCandidatesStateDefaults
})
@Injectable()
export class ExamCandidatesState {
  constructor(
    private readonly loadExamCandidatesService: LoadExamCandidatesGQL,
    private readonly httpClient: HttpClient,
    private readonly toastService: ToastsService,
    private readonly translateService: TranslateService,
    private readonly jobService: JobGQL,
    private readonly createMasterDocumentService: GenerateMasterAccessDocumentGQL,
    private readonly deleteCandidatesService: DeleteCandidatesGQL
  ) {}

  @Action(Act.LoadExamCandidates)
  public loadExamCandidates(
    ctx: StateContext<ExamCandidatesStateModel>,
    { examId }: Act.LoadExamCandidates
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, query: { state: 'loading' } }
    });

    return this.loadExamCandidatesService.fetch({ examId }).pipe(
      takeGraphQLResult(),
      switchMap(res =>
        ctx.dispatch(new Act.LoadExamCandidatesSuccess(res.exam.candidates))
      ),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.LoadExamCandidatesFailure(parseError(err)))
      )
    );
  }

  @Action(Act.LoadExamCandidatesSuccess)
  public loadExamCandidatesSuccess(
    ctx: StateContext<ExamCandidatesStateModel>,
    { candidates }: Act.LoadExamCandidatesSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: candidates,
        query: { state: 'success' }
      }
    });
  }

  @Action(Act.LoadExamCandidatesFailure)
  public loadExamCandidatesFailure(
    ctx: StateContext<ExamCandidatesStateModel>,
    { error }: Act.LoadExamCandidatesFailure
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        query: { state: 'failure', error }
      }
    });
  }

  @Action(Act.SetSelectedCandidateIds)
  public setSelectedCandidateIds(
    ctx: StateContext<ExamCandidatesStateModel>,
    { ids }: Act.SetSelectedCandidateIds
  ): void {
    ctx.patchState({ selectedIds: ids });
  }

  @Action(Act.ResetSelectedCandidateIds)
  public resetSelectedCandidateIds(
    ctx: StateContext<ExamCandidatesStateModel>
  ): void {
    ctx.patchState({ selectedIds: [] });
  }

  @Action(Act.ImportCandidates)
  public importCandidates(
    ctx: StateContext<ExamCandidatesStateModel>,
    { file, examId }: Act.ImportCandidates
  ): Observable<void> {
    ctx.patchState({
      importMutation: { requestState: 'loading' }
    });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('exam_id', examId);

    return this.httpClient
      .post<{ job_id: number }>('/api/v2/candidates/upload', formData)
      .pipe(
        switchMap(res => this.pollJobAndDownload(res.job_id, false)),
        switchMap(() =>
          this.loadExamCandidatesService
            .fetch({ examId })
            .pipe(takeGraphQLResult())
        ),
        switchMap(res =>
          ctx.dispatch([
            new Act.ImportCandidatesSuccess(),
            new Act.LoadExamCandidatesSuccess(res.exam.candidates)
          ])
        ),
        catchError((err: unknown) =>
          ctx.dispatch([
            new Act.ImportCandidatesFailure(parseError(err)),
            new Act.LoadExamCandidatesFailure(parseError(err))
          ])
        )
      );
  }

  @Action(Act.ImportCandidatesSuccess)
  public importCandidatesSuccess(
    ctx: StateContext<ExamCandidatesStateModel>
  ): void {
    ctx.patchState({
      importMutation: { requestState: 'success' }
    });
  }

  @Action(Act.ImportCandidatesFailure)
  public importCandidatesFailure(
    ctx: StateContext<ExamCandidatesStateModel>,
    { error }: Act.ImportCandidatesFailure
  ): void {
    ctx.patchState({
      importMutation: { requestState: 'failure', error }
    });
  }

  @Action(Act.ResetMutation)
  public resetMutation(ctx: StateContext<ExamCandidatesStateModel>): void {
    ctx.patchState({
      importMutation: { requestState: 'initial', error: undefined }
    });
  }

  @Action(Act.SetExamCandidatesOrder)
  public setExamCandidatesOrder(
    ctx: StateContext<ExamCandidatesStateModel>,
    { order }: Act.SetExamCandidatesOrder
  ): void {
    ctx.patchState({ order });
  }

  @Action(Act.ResetExamCandidatesOrder)
  public resetExamCandidatesOrder(
    ctx: StateContext<ExamCandidatesStateModel>
  ): void {
    ctx.patchState({ order: examCandidatesStateDefaults.order });
  }

  @Action(Act.CreateAccessDocuments)
  public createAccessDocuments(
    ctx: StateContext<ExamCandidatesStateModel>,
    { payload }: Act.CreateAccessDocuments
  ): Observable<void> {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        mutation: { state: 'loading' }
      }
    });

    return this.httpClient
      .post<{ job_id: number }>(`/api/v2/candidates/access_papers`, {
        ...payload
      })
      .pipe(
        switchMap(res => this.pollJobAndDownload(res.job_id)),
        switchMap(() => ctx.dispatch(new Act.CreateAccessDocumentsSuccess())),
        catchError((err: unknown) => {
          return ctx.dispatch(
            new Act.CreateAccessDocumentsFailure(parseError(err))
          );
        })
      );
  }

  @Action(Act.CreateAccessDocumentsSuccess)
  public createAccessDocumentsSuccess(
    ctx: StateContext<ExamCandidatesStateModel>
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        mutation: {
          state: 'success'
        }
      }
    });
  }

  @Action(Act.CreateAccessDocumentsFailure)
  public createAccessDocumentsFailure(
    ctx: StateContext<ExamCandidatesStateModel>,
    { error }: Act.CreateAccessDocumentsFailure
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        mutation: {
          state: 'failure',
          error
        }
      }
    });

    this.toastService.addWarning(
      this.translateService.instant('error_modal.title')
    );
  }

  @Action(Act.DeleteCandidates)
  public deleteCandidates(
    ctx: StateContext<ExamCandidatesStateModel>,
    { examId, candidateIds }: Act.DeleteCandidates
  ): Observable<void> {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        mutation: { state: 'loading' },
        query: { ...ctx.getState().items.query, state: 'loading' }
      }
    });

    return this.deleteCandidatesService
      .mutate({ examId, ids: candidateIds })
      .pipe(
        takeGraphQLResult(),
        map(res => res?.destroyCandidates?.jobId),
        switchMap(jobId => this.pollJobAndDownload(Number(jobId), false)),
        switchMap(() =>
          ctx.dispatch([new Act.DeleteCandidatesSuccess(candidateIds)])
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.DeleteCandidatesFailure(parseError(err)))
        )
      );
  }

  @Action(Act.DeleteCandidatesSuccess)
  public deleteCandidatesSuccess(
    ctx: StateContext<ExamCandidatesStateModel>,
    { candidateIds }: Act.DeleteCandidatesSuccess
  ): void {
    const state = ctx.getState();
    const remainingCandidates = state.items.data?.filter(
      candidate => !candidateIds.includes(candidate.id.toString())
    );

    ctx.patchState({
      items: {
        ...state.items,
        data: remainingCandidates,
        query: { state: 'success' },
        mutation: {
          state: 'success'
        }
      }
    });

    this.toastService.addSuccess(
      this.translateService.instant('toast_success_messages.destroy', {
        resource: this.translateService.instant('common.models.candidate')
      })
    );
  }

  @Action(Act.DeleteCandidatesFailure)
  public deleteCandidatesFailure(
    ctx: StateContext<ExamCandidatesStateModel>,
    { error }: Act.DeleteCandidatesFailure
  ): void {
    const state = ctx.getState();

    ctx.patchState({
      items: {
        ...state.items,
        mutation: {
          state: 'failure',
          error
        }
      }
    });

    this.toastService.addWarning(
      this.translateService.instant('error_modal.title')
    );
  }

  @Action(Act.CreateMasterDocument)
  public createMasterDocument(
    ctx: StateContext<ExamCandidatesStateModel>,
    { payload }: Act.CreateMasterDocument
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    return this.createMasterDocumentService
      .mutate({
        examId: payload.examId,
        maxLogins: payload.maxLogins,
        validFrom: payload.validFrom,
        validTo: payload.validTo
      })
      .pipe(
        takeGraphQLResult(),
        map(res => res?.generateMasterAccessDocument),
        switchMap(doc => {
          if (doc?.successful === true) {
            if (doc.filePath !== undefined && doc.filePath !== null) {
              window.open(doc.filePath, '_blank');
            }

            return ctx.dispatch(new Act.CreateMasterDocumentSuccess());
          }

          return throwError(
            () => new Error(doc?.error ?? 'Master document creation failed')
          );
        }),
        catchError((err: unknown) => {
          return ctx.dispatch(
            new Act.CreateMasterDocumentFailure(parseError(err))
          );
        })
      );
  }

  @Action(Act.CreateMasterDocumentSuccess)
  public createMasterDocumentSuccess(
    ctx: StateContext<ExamCandidatesStateModel>
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        mutation: {
          state: 'success'
        }
      }
    });
  }

  @Action(Act.CreateMasterDocumentFailure)
  public createMasterDocumentFailure(
    ctx: StateContext<ExamCandidatesStateModel>,
    { error }: Act.CreateMasterDocumentFailure
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        mutation: {
          state: 'failure',
          error
        }
      }
    });

    this.toastService.addWarning(
      this.translateService.instant('error_modal.title')
    );
  }

  private pollJobAndDownload(
    jobId: number,
    withResult = true
  ): Observable<void> {
    return interval(2000).pipe(
      switchMap(() =>
        this.jobService.fetch({ id: jobId }).pipe(takeGraphQLResult())
      ),
      map(res => {
        const job = res.job;

        if (!job) {
          throw new Error('Job not found');
        }

        if (job.status === 'failed') {
          throw new Error('Job failed');
        }

        return job;
      }),
      filter(job => job.status === 'finished'),
      take(1),
      tap(job => {
        if (withResult) {
          this.handleJobResult(job.result);
        }
      }),
      mapTo(void 0),
      catchError((err: unknown) => {
        throw err instanceof Error
          ? err
          : new Error('Failed to fetch job status');
      })
    );
  }

  private handleJobResult(result: Job['result']): void {
    if (result === undefined || result === null) {
      throw new Error('Missing job result for access documents');
    }

    if (result !== undefined && result !== null) {
      const parsed: { access_papers_path: string } = JSON.parse(result);

      const url = parsed.access_papers_path;

      if (url) {
        window.open(url, '_blank');
      }
    }
  }
}
